import React, { useEffect, useRef, useState } from 'react'
import HttpClient from '../../../services/HttpClient'

/**
 * Selector de emisor: Personaje (autocomplete) | Daimyo | NPC (texto libre).
 */
export default function MissionIssuerPicker({
  territorySlug,
  issuerMode,
  issuerCharacter,
  issuerLabel,
  canChooseDaimyo,
  hasDaimyo,
  hanName,
  onModeChange,
  onCharacterChange,
  onLabelChange,
  disabled = false,
  radioName = 'mission-issuer-mode',
}) {
  const [query, setQuery] = useState(issuerCharacter?.name || '')
  const [suggestions, setSuggestions] = useState([])
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const debounceRef = useRef(null)
  const wrapRef = useRef(null)

  useEffect(() => {
    if (issuerMode === 'character' && issuerCharacter?.name) {
      setQuery(issuerCharacter.name)
    }
    if (issuerMode !== 'character') {
      setSuggestions([])
      setOpen(false)
    }
  }, [issuerMode, issuerCharacter?.characterId, issuerCharacter?.name])

  useEffect(() => {
    if (issuerMode !== 'character' || issuerCharacter) return undefined

    const q = query.trim()
    if (q.length < 2) {
      setSuggestions([])
      setOpen(false)
      return undefined
    }

    clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(async () => {
      setLoading(true)
      try {
        const { data } = await HttpClient().get('/api/v1/missions/issuer-candidates', {
          params: { territorySlug, q },
        })
        const rows = Array.isArray(data) ? data : []
        setSuggestions(rows)
        setOpen(rows.length > 0)
      } catch {
        setSuggestions([])
        setOpen(false)
      } finally {
        setLoading(false)
      }
    }, 250)

    return () => clearTimeout(debounceRef.current)
  }, [query, territorySlug, issuerMode, issuerCharacter])

  useEffect(() => {
    const onDocClick = (event) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', onDocClick)
    return () => document.removeEventListener('mousedown', onDocClick)
  }, [])

  const pick = (candidate) => {
    onCharacterChange?.({
      characterId: Number(candidate.characterId),
      name: candidate.name,
      slug: candidate.slug || null,
      clanName: candidate.clanName || null,
      rankName: candidate.rankName || null,
    })
    setQuery(candidate.name)
    setOpen(false)
    setSuggestions([])
  }

  const clearCharacter = () => {
    onCharacterChange?.(null)
    setQuery('')
    setSuggestions([])
    setOpen(false)
  }

  const modes = [
    { id: 'character', label: 'Personaje' },
    ...(canChooseDaimyo ? [{ id: 'daimyo', label: 'Daimyo' }] : []),
    { id: 'npc', label: 'NPC' },
  ]

  return (
    <div className="mission-create__issuer-block mission-create__issuer-picker">
      <span className="field-label">Emisor</span>
      <div className="mission-create__issuer-modes" role="radiogroup" aria-label="Tipo de emisor">
        {modes.map((mode) => (
          <label
            key={mode.id}
            className={`mission-create__issuer-mode ${
              issuerMode === mode.id ? 'mission-create__issuer-mode--active' : ''
            }`}
          >
            <input
              type="radio"
              name={radioName}
              value={mode.id}
              checked={issuerMode === mode.id}
              disabled={disabled}
              onChange={() => onModeChange?.(mode.id)}
            />
            <span>{mode.label}</span>
          </label>
        ))}
      </div>

      {issuerMode === 'character' && (
        <div className="mission-invite-autocomplete" ref={wrapRef}>
          <div className="mission-invite-autocomplete__field">
            <input
              className="field-input"
              type="text"
              value={query}
              onChange={(event) => {
                clearCharacter()
                setQuery(event.target.value)
              }}
              onFocus={() => {
                if (suggestions.length > 0) setOpen(true)
              }}
              placeholder="Buscar personaje por nombre"
              disabled={disabled}
              autoComplete="off"
              aria-autocomplete="list"
              aria-expanded={open}
            />
            {loading && <span className="mission-invite-autocomplete__hint">Buscando…</span>}
            {issuerCharacter && (
              <p className="mission-create__issuer-selected">
                {issuerCharacter.name}
                {issuerCharacter.clanName ? ` · ${issuerCharacter.clanName}` : ''}
                {issuerCharacter.rankName ? ` · ${issuerCharacter.rankName}` : ''}
                <button
                  type="button"
                  className="mission-create__issuer-clear"
                  onClick={clearCharacter}
                  disabled={disabled}
                >
                  Cambiar
                </button>
              </p>
            )}
            {open && suggestions.length > 0 && (
              <ul className="mission-invite-autocomplete__list" role="listbox">
                {suggestions.map((candidate) => (
                  <li key={candidate.characterId} role="option">
                    <button type="button" onClick={() => pick(candidate)}>
                      <span className="mission-create__issuer-opt-name">{candidate.name}</span>
                      {(candidate.clanName || candidate.rankName) && (
                        <span className="mission-create__issuer-opt-meta">
                          {[candidate.clanName, candidate.rankName].filter(Boolean).join(' · ')}
                        </span>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {issuerMode === 'daimyo' && (
        <div>
          {hasDaimyo ? (
            <p className="mission-create__issuer-note">
              Se publicará como Daimyo{hanName ? ` de ${hanName}` : ''}.
            </p>
          ) : (
            <p className="mission-create__issuer-note mission-create__issuer-note--warn">
              Este Han no tiene Daimyo asignado. Asigna uno en Admin → Han o elige otro emisor.
            </p>
          )}
        </div>
      )}

      {issuerMode === 'npc' && (
        <div className="field-row">
          <input
            id="mission-issuer-label"
            className="field-input"
            type="text"
            value={issuerLabel || ''}
            maxLength={120}
            placeholder="Nombre del NPC (ej. Arai Kiyoshi)"
            disabled={disabled}
            onChange={(e) => onLabelChange?.(e.target.value)}
          />
          <p className="mission-create__issuer-note">
            NPC solo para esta misión; retrato opcional vía{' '}
            <code>{'{slug}'}-sm.png</code> en assets.
          </p>
        </div>
      )}
    </div>
  )
}
